import { TIME_AXIS_HEIGHT_PX } from "../constants";
import { axisFont } from "../text/axisFont";
import { createZonedTime } from "./zonedTime";
import { createTimeLabelFormatter, createCrosshairTimeFormatter } from "./timeFormat";
import { computeTimeWeights } from "./timeWeights";
import { buildTimeTicks } from "./timeTicks";

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const keepUtc = (utcMs) => utcMs;

/**
 * Time axis under the pane: labels from seconds to years in the user's
 * time zone. Bar weights are cached until the bars or the zone change.
 * Right of the last bar the axis and the grid go on with future bar times.
 */
export default class TimeAxis {
  constructor() {
    this.timezone = null;
    this.formatTime = createTimeLabelFormatter(null);
    this.formatCrosshair = createCrosshairTimeFormatter(null);
    this.bars = [];
    this.barDurationMs = 0;
    this.weights = null;
    this.toLocalMs = keepUtc;
  }

  setTimezone(timezone, locale) {
    this.timezone = timezone;
    this.formatTime = createTimeLabelFormatter(locale);
    this.formatCrosshair = createCrosshairTimeFormatter(locale);
    this.weights = null;
  }

  setBars(bars, barDurationMs) {
    this.bars = bars;
    this.barDurationMs = barDurationMs;
    this.weights = null;
  }

  // Bars past the last one are one bar duration apart
  timeAt(index) {
    const lastIndex = this.bars.length - 1;

    if (index <= lastIndex) return this.bars[index].time;

    return this.bars[lastIndex].time + (index - lastIndex) * this.barDurationMs;
  }

  /** @param {number} [count] - bars to weigh, future ones included */
  ensureWeights(count = this.bars.length) {
    if (this.weights && this.weights.length >= count) return;

    // Daily and longer bars carry an exchange date, which is the same in every zone
    this.toLocalMs =
      this.barDurationMs >= MS_PER_DAY ? keepUtc : createZonedTime(this.timezone);
    this.weights = computeTimeWeights(
      Math.max(count, this.bars.length),
      (index) => this.timeAt(index),
      this.barDurationMs,
      this.toLocalMs,
    );
  }

  /**
   * Time under the crosshair in the user's zone (references 02, 07–09).
   *
   * @param {number} utcMs
   * @returns {string}
   */
  formatCrosshairTime(utcMs) {
    this.ensureWeights();

    return this.formatCrosshair(this.toLocalMs(utcMs), this.barDurationMs);
  }

  /**
   * @param {object} params
   * @param {{ first: number, last: number }} params.range - visible bars
   * @param {object} params.timeScale
   * @param {(text: string, font: string) => number} params.measure
   * @param {string} params.fontFamily
   */
  buildTicks({ range, timeScale, measure, fontFamily }) {
    if (!this.bars.length) return [];

    // The empty space right of the last bar gets labels and grid lines too
    const lastIndex = Math.max(range.last, Math.ceil(timeScale.xToIndex(timeScale.width)));

    this.ensureWeights(lastIndex + 1);

    return buildTimeTicks({
      weights: this.weights,
      range: { first: range.first, last: lastIndex },
      timeScale,
      barDurationMs: this.barDurationMs,
      measureLabel: (text, isBold) => measure(text, axisFont(fontFamily, isBold)),
      formatLabel: (index, unit) => this.formatTime(this.toLocalMs(this.timeAt(index)), unit),
    });
  }
}

/**
 * Draws time labels centred under their bars.
 *
 * @param {object} textLayer
 * @param {Array<{ x: number, text: string, isBold: boolean }>} ticks
 * @param {number} plotHeight - all panes above the time axis, CSS px
 * @param {{ colors: object, fontFamily: string }} theme
 * @param {boolean} isSnappedX - false keeps the labels sub-pixel (while the view follows time)
 */
export const drawTimeAxis = (textLayer, ticks, plotHeight, theme, isSnappedX) => {
  const y = plotHeight + TIME_AXIS_HEIGHT_PX / 2;

  ticks.forEach(({ x, text, isBold }) => {
    textLayer.drawText(text, x, y, {
      font: axisFont(theme.fontFamily, isBold),
      color: theme.colors.axisText.css,
      align: "center",
      isSnappedX,
    });
  });
};
